import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { loadRazorpayScript } from '../../utils/loadRazorpay';

export default function Checkout() {
  const { items: contextItems, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Direct localStorage fallback — ensures items are NEVER lost even on first render
  const lsRaw = localStorage.getItem('ca_cart');
  const lsItems = React.useMemo(() => {
    try { return lsRaw ? JSON.parse(lsRaw) : []; } catch { return []; }
  }, [lsRaw]);

  // Use whichever has items — context or localStorage
  const items = contextItems.length > 0 ? contextItems : lsItems;
  const itemsTotal = items.reduce((sum, i) => sum + (i.price * i.qty), 0);

  const [address, setAddress] = useState({
    fullName: user?.name || '',
    phone: user?.phone || '',
    line1: '', line2: '', city: '', state: '', pincode: '',
    lat: null, lng: null,
  });
  const [locating, setLocating] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [pmConfig, setPmConfig] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [availablePromos, setAvailablePromos] = useState([]);
  const [showAllPromos, setShowAllPromos] = useState(false);
  const [errors, setErrors] = useState({});

  const updateAddress = (field, value) => {
    setAddress((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  // Fetch available promo codes for customers
  useEffect(() => {
    api.get('/promos/available')
      .then((res) => setAvailablePromos(res.data || []))
      .catch(() => setAvailablePromos([]));
  }, []);

  // Fetch enabled payment gateways from admin settings
  useEffect(() => {
    let active = true;
    const loadPaymentMethods = () => {
      api.get('/settings/public-payment')
      .then((res) => {
        if (!active) return;
        setPmConfig(res.data);
        const cfg = res.data;
        if (cfg.cod?.isEnabled) setPaymentMethod('cod');
        else if (cfg.razorpay?.isEnabled) setPaymentMethod('razorpay');
        else if (cfg.stripe?.isEnabled) setPaymentMethod('stripe');
        else if (cfg.paypal?.isEnabled) setPaymentMethod('paypal');
        else if (cfg.upi?.isEnabled) setPaymentMethod('upi');
        else if (cfg.bankTransfer?.isEnabled) setPaymentMethod('bank_transfer');
      })
      .catch(() => {
        if (active) setPmConfig({}); // fallback — COD always available
      });
    };

    loadPaymentMethods();
    const intervalId = window.setInterval(loadPaymentMethods, 30000);
    window.addEventListener('focus', loadPaymentMethods);
    return () => {
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', loadPaymentMethods);
    };
  }, []);

  // Auto-fill address from logged in user
  useEffect(() => {
    if (user) {
      const saved = user.addresses?.find((item) => item.isDefault) || user.addresses?.[0];
      setAddress((prev) => ({
        ...prev,
        ...(saved && !prev.line1 ? {
          fullName: saved.fullName || user.name || '', phone: saved.phone || user.phone || '',
          line1: saved.line1 || '', line2: saved.line2 || '', city: saved.city || '',
          state: saved.state || '', pincode: saved.pincode || '', lat: saved.lat ?? null, lng: saved.lng ?? null,
        } : {}),
        fullName: prev.fullName || user.name || '',
        phone: prev.phone || user.phone || '',
      }));
    }
  }, [user]);

  const detectLocation = () => {
    if (!navigator.geolocation) { toast.error('Geolocation not supported by this browser'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        let line1 = '', city = '', state = '', pincode = '';
        try {
          const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
          const d = await r.json();
          const a = d.address || {};
          line1 = [a.building, a.house_number, a.road, a.suburb].filter(Boolean).join(', ');
          city  = a.city || a.town || a.village || '';
          state = a.state || '';
          pincode = a.postcode || '';
        } catch (_) {}
        setAddress((prev) => ({
          ...prev, lat, lng,
          line1:   prev.line1   || line1,
          city:    prev.city    || city,
          state:   prev.state   || state,
          pincode: prev.pincode || pincode,
        }));
        fetchQuote(lat, lng);
        setLocating(false);
        toast.success('Location detected & address auto-filled!');
      },
      () => { toast.error('Location access denied'); setLocating(false); }
    );
  };

  const fetchQuote = async (lat, lng) => {
    setQuoting(true);
    try {
      const { data } = await api.post('/shipping/quote', { lat, lng, itemsTotal });
      setQuote(data);
    } catch (_) {}
    finally { setQuoting(false); }
  };

  const freeShippingThreshold = pmConfig?.shipping?.freeShippingAboveAmount || 0;
  const defaultShippingCost = freeShippingThreshold > 0 && itemsTotal >= freeShippingThreshold
    ? 0
    : (pmConfig?.shipping?.baseFee ?? 40);
  const shippingCost = quote?.shippingCost ?? defaultShippingCost;
  const codFee = paymentMethod === 'cod' ? (pmConfig?.cod?.extraFee ?? 0) : 0;
  const discountTotal = appliedPromo?.discount || 0;
  const discountedItemsTotal = Math.max(0, itemsTotal - discountTotal);
  const taxTotal = Math.round((discountedItemsTotal * (pmConfig?.taxPercent || 0)) / 100);
  const grandTotal = discountedItemsTotal + shippingCost + codFee + taxTotal;

  const applyPromo = async (codeOrEvent) => {
    let targetCode = promoInput;
    if (typeof codeOrEvent === 'string') {
      targetCode = codeOrEvent;
    } else if (codeOrEvent?.preventDefault) {
      codeOrEvent.preventDefault();
    }
    targetCode = String(targetCode || '').trim().toUpperCase();
    if (!targetCode) return;
    setPromoLoading(true);
    try {
      const { data } = await api.post('/promos/validate', { code: targetCode, itemsTotal });
      setAppliedPromo(data);
      setPromoInput(data.code);
      toast.success(`${data.code} applied! Saved ₹${data.discount.toFixed(2)}`);
    } catch (error) {
      setAppliedPromo(null);
      toast.error(error.response?.data?.message || 'Promo code could not be applied');
    } finally {
      setPromoLoading(false);
    }
  };

  const handlePlaceOrder = async () => {
    const newErrors = {};

    if (!address.fullName?.trim()) {
      newErrors.fullName = 'Please enter full name';
    }

    const normalizedPhone = String(address.phone || '').replace(/[\s()-]/g, '');
    if (!address.phone?.trim()) {
      newErrors.phone = 'Please enter phone number';
    } else if (!/^(?:\+?91)?[6-9]\d{9}$/.test(normalizedPhone)) {
      newErrors.phone = 'Enter valid 10-digit Indian mobile number';
    }

    if (!address.line1?.trim()) {
      newErrors.line1 = 'Please enter street address / house no.';
    }

    if (!address.city?.trim()) {
      newErrors.city = 'Please enter city';
    }

    if (!address.state?.trim()) {
      newErrors.state = 'Please enter state';
    }

    if (!address.pincode?.trim()) {
      newErrors.pincode = 'Please enter pincode';
    } else if (!/^\d{6}$/.test(String(address.pincode).trim())) {
      newErrors.pincode = 'Enter valid 6-digit pincode';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fill in all highlighted required address fields');
      const addrEl = document.getElementById('checkout-shipping-address');
      if (addrEl) addrEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setErrors({});
    setPlacing(true);

    const orderItems = items.map((i) => ({
      productId: i.productId,
      variantId: i.variantId || null,
      qty: i.qty,
    }));

    const orderPayload = {
      items: orderItems,
      shippingAddress: { ...address },
      paymentMethod,
      promoCode: appliedPromo?.code || undefined,
    };

    try {
      /* COD, UPI and bank transfer create an order immediately. */
      if (paymentMethod === 'cod' || paymentMethod === 'upi' || paymentMethod === 'bank_transfer') {
        const { data } = await api.post('/orders', orderPayload);
        clearCart();
        toast.success(
          paymentMethod === 'cod'
            ? 'Order placed! Pay cash on delivery.'
            : 'Order placed! Complete payment using the selected instructions.'
        );
        navigate(`/orders/${data._id}`);
        return;
      }

      /* ── RAZORPAY ── */
      if (paymentMethod === 'razorpay') {
        const { data: orderRes } = await api.post('/orders', orderPayload);
        const orderId = orderRes._id;

        const { data: rzpOrder } = await api.post('/payments/razorpay/create-order', {
          orderId, amount: Math.round(orderRes.totalAmount),
        });

        const loaded = await loadRazorpayScript();
        if (!loaded) {
          toast.error('Razorpay SDK failed to load. Please try COD.');
          setPlacing(false);
          return;
        }

        const options = {
          key: rzpOrder.keyId || pmConfig?.razorpay?.keyId,
          amount: rzpOrder.amount,
          currency: 'INR',
          name: 'Chandana Aura',
          description: `Order #${orderId}`,
          order_id: rzpOrder.id,
          handler: async (response) => {
            try {
              await api.post('/payments/razorpay/verify', {
                orderId,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              clearCart();
              toast.success('Payment verified! Order confirmed ✓');
              navigate(`/orders/${orderId}`);
            } catch (_) {
              toast.error('Payment verification failed. Contact support.');
            }
          },
          modal: { ondismiss: () => setPlacing(false) },
          prefill: { name: address.fullName, contact: address.phone, email: user?.email || '' },
          theme: { color: '#C49A45' },
        };

        new window.Razorpay(options).open();
        setPlacing(false);
        return;
      }

      if (paymentMethod === 'stripe') {
        const { data: orderRes } = await api.post('/orders', orderPayload);
        const { data: stripeSession } = await api.post('/payments/stripe/create-session', { orderId: orderRes._id });
        window.location.assign(stripeSession.checkoutUrl);
        return;
      }

      if (paymentMethod === 'paypal') {
        const { data: orderRes } = await api.post('/orders', orderPayload);
        const { data: paypalOrder } = await api.post('/payments/paypal/create-order', { orderId: orderRes._id });
        window.location.assign(paypalOrder.approvalUrl);
        return;
      }

      // Fallback
      const { data } = await api.post('/orders', orderPayload);
      clearCart();
      toast.success('Order placed!');
      navigate(`/orders/${data._id}`);

    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to place order. Please try again.');
    } finally {
      setPlacing(false);
    }
  };

  /* ─── Empty Cart Screen ─── */
  if (items.length === 0) {
    return (
      <div className="bg-[#FAF8F5] min-h-screen flex items-center justify-center py-24">
        <div className="text-center space-y-4 max-w-sm px-4">
          <div className="w-16 h-16 rounded-full bg-[#F5EFEB] flex items-center justify-center text-[#C49A45] mx-auto border border-[#E8DFD8]">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 01-8 0"/>
            </svg>
          </div>
          <h2 className="font-heading text-2xl font-bold text-[#2B1810]">Your Cart is Empty</h2>
          <p className="text-xs text-[#8A796C]">Add items to your cart before checking out.</p>
          <Link to="/shop" className="bg-[#C49A45] hover:bg-[#B08736] text-white font-bold text-xs uppercase tracking-widest px-8 py-3 rounded-lg shadow inline-block transition-colors">
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  /* ─── Payment Method Card ─── */
  const PMCard = ({ id, title, note, children }) => (
    <div
      onClick={() => setPaymentMethod(id)}
      className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
        paymentMethod === id
          ? 'border-[#C49A45] bg-amber-50/60 shadow-sm'
          : 'border-[#E0D6CC] bg-white hover:border-[#C49A45]/40 hover:bg-[#FAF8F5]'
      }`}
    >
      <div className="flex items-center gap-4">
        <div className="shrink-0">
          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
            paymentMethod === id ? 'border-[#C49A45] bg-[#C49A45]' : 'border-[#C4B5A5]'
          }`}>
            {paymentMethod === id && <div className="w-2 h-2 rounded-full bg-white" />}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <span className="font-bold text-xs uppercase tracking-wider text-[#2B1810]">{title}</span>
            {note && <span className="text-[11px] font-semibold text-[#8A796C] shrink-0">{note}</span>}
          </div>
          {paymentMethod === id && children && (
            <div className="mt-2">{children}</div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-[#FAF8F5] min-h-screen py-10">
      <div className="max-w-6xl mx-auto px-4 space-y-8">

        {/* Header */}
        <div className="text-center space-y-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C49A45]">Secure Checkout</span>
          <h1 className="font-heading text-3xl font-bold text-[#2B1810]">Complete Your Order</h1>
        </div>

        <div className="grid md:grid-cols-3 gap-8 items-start">
          <div className="md:col-span-2 space-y-6">

            {/* ── STEP 1: ADDRESS ── */}
            <div id="checkout-shipping-address" className="bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm space-y-4 scroll-mt-28">
              <div className="flex items-center justify-between border-b border-[#E8DFD8] pb-3">
                <h3 className="font-heading text-sm font-bold text-[#2B1810]">1. Shipping Address</h3>
                <span className="text-[10px] font-bold text-[#C49A45] bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">Step 1 / 2</span>
              </div>

              {user?.addresses?.length > 0 && (
                <div>
                  <label className="label">Choose a Saved Address</label>
                  <select
                    className="input-field"
                    defaultValue={user.addresses.find((item) => item.isDefault)?._id || user.addresses[0]._id}
                    onChange={(e) => {
                      const saved = user.addresses.find((item) => item._id === e.target.value);
                      if (saved) {
                        setAddress({
                          fullName: saved.fullName || '', phone: saved.phone || '', line1: saved.line1 || '',
                          line2: saved.line2 || '', city: saved.city || '', state: saved.state || '',
                          pincode: saved.pincode || '', lat: saved.lat ?? null, lng: saved.lng ?? null,
                        });
                        setErrors({});
                      }
                    }}
                  >
                    {user.addresses.map((saved) => <option key={saved._id} value={saved._id}>{saved.label || 'Address'}{saved.isDefault ? ' (Default)' : ''} - {saved.city}</option>)}
                  </select>
                </div>
              )}

              {/* GPS Button */}
              <button
                type="button"
                onClick={detectLocation}
                disabled={locating}
                className="flex items-center gap-2 bg-[#FAF8F5] hover:bg-[#F5EFEB] border border-[#C49A45] text-[#2B1810] font-bold text-[11px] uppercase tracking-wider py-2.5 px-4 rounded-lg transition-colors w-full md:w-auto justify-center"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C49A45" strokeWidth="2.5">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                </svg>
                {locating ? 'Detecting GPS...' : address.lat ? '✓ Re-detect My GPS Location' : 'Use My GPS & Auto-fill Address'}
              </button>

              {address.lat && (
                <p className="text-[11px] text-emerald-700 font-semibold bg-emerald-50 border border-emerald-200 p-2.5 rounded-lg">
                  ✓ GPS captured ({address.lat.toFixed(4)}, {address.lng.toFixed(4)}) — Address auto-filled below
                </p>
              )}

              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="label">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    autoComplete="name"
                    className={`input-field ${
                      errors.fullName ? '!border-red-500 !bg-red-50/20 text-red-900 focus:!border-red-500 focus:!ring-red-500' : ''
                    }`}
                    value={address.fullName}
                    onChange={(e) => updateAddress('fullName', e.target.value)}
                    placeholder="Ananya Sharma"
                  />
                  {errors.fullName && (
                    <p className="text-[11px] font-semibold text-red-600 mt-1 flex items-center gap-1">
                      <span>⚠</span> {errors.fullName}
                    </p>
                  )}
                </div>

                <div>
                  <label className="label">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    className={`input-field ${
                      errors.phone ? '!border-red-500 !bg-red-50/20 text-red-900 focus:!border-red-500 focus:!ring-red-500' : ''
                    }`}
                    value={address.phone}
                    onChange={(e) => updateAddress('phone', e.target.value)}
                    placeholder="9876543210"
                  />
                  {errors.phone && (
                    <p className="text-[11px] font-semibold text-red-600 mt-1 flex items-center gap-1">
                      <span>⚠</span> {errors.phone}
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="label">
                    Address Line 1 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={`input-field ${
                      errors.line1 ? '!border-red-500 !bg-red-50/20 text-red-900 focus:!border-red-500 focus:!ring-red-500' : ''
                    }`}
                    value={address.line1}
                    onChange={(e) => updateAddress('line1', e.target.value)}
                    placeholder="Flat no., House no., Building, Street name"
                  />
                  {errors.line1 && (
                    <p className="text-[11px] font-semibold text-red-600 mt-1 flex items-center gap-1">
                      <span>⚠</span> {errors.line1}
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="label">Address Line 2 (optional)</label>
                  <input
                    type="text"
                    className="input-field"
                    value={address.line2}
                    onChange={(e) => updateAddress('line2', e.target.value)}
                    placeholder="Landmark, Area, Colony"
                  />
                </div>

                <div>
                  <label className="label">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={`input-field ${
                      errors.city ? '!border-red-500 !bg-red-50/20 text-red-900 focus:!border-red-500 focus:!ring-red-500' : ''
                    }`}
                    value={address.city}
                    onChange={(e) => updateAddress('city', e.target.value)}
                    placeholder="Mysuru"
                  />
                  {errors.city && (
                    <p className="text-[11px] font-semibold text-red-600 mt-1 flex items-center gap-1">
                      <span>⚠</span> {errors.city}
                    </p>
                  )}
                </div>

                <div>
                  <label className="label">
                    State <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={`input-field ${
                      errors.state ? '!border-red-500 !bg-red-50/20 text-red-900 focus:!border-red-500 focus:!ring-red-500' : ''
                    }`}
                    value={address.state}
                    onChange={(e) => updateAddress('state', e.target.value)}
                    placeholder="Karnataka"
                  />
                  {errors.state && (
                    <p className="text-[11px] font-semibold text-red-600 mt-1 flex items-center gap-1">
                      <span>⚠</span> {errors.state}
                    </p>
                  )}
                </div>

                <div>
                  <label className="label">
                    Pincode <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    className={`input-field ${
                      errors.pincode ? '!border-red-500 !bg-red-50/20 text-red-900 focus:!border-red-500 focus:!ring-red-500' : ''
                    }`}
                    value={address.pincode}
                    onChange={(e) => updateAddress('pincode', e.target.value)}
                    placeholder="570001"
                  />
                  {errors.pincode && (
                    <p className="text-[11px] font-semibold text-red-600 mt-1 flex items-center gap-1">
                      <span>⚠</span> {errors.pincode}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* ── STEP 2: PAYMENT ── */}
            <div className="bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm space-y-4">
              <div className="flex items-center justify-between border-b border-[#E8DFD8] pb-3">
                <h3 className="font-heading text-sm font-bold text-[#2B1810]">2. Payment Method</h3>
                <span className="text-[10px] font-bold text-[#C49A45] bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">Step 2 / 2</span>
              </div>

              <div className="space-y-3">

                {pmConfig?.cod?.isEnabled !== false && (
                  <PMCard
                    id="cod"
                    title="Cash on Delivery (COD)"
                    note={pmConfig?.cod?.extraFee > 0 ? `+₹${pmConfig.cod.extraFee}` : null}
                  />
                )}

                {/* Razorpay — if enabled in admin */}
                {pmConfig?.razorpay?.isEnabled && (
                  <PMCard
                    id="razorpay"
                    title="Razorpay"
                    note="Cards, UPI & more"
                  />
                )}

                {pmConfig?.stripe?.isEnabled && (
                  <PMCard
                    id="stripe"
                    title="Stripe"
                    note="Cards & wallets"
                  />
                )}

                {pmConfig?.paypal?.isEnabled && (
                  <PMCard
                    id="paypal"
                    title="PayPal"
                    note="PayPal checkout"
                  />
                )}

                {pmConfig?.upi?.isEnabled && (
                  <PMCard
                    id="upi"
                    title="Direct UPI Payment"
                    note={pmConfig.upi.vpa}
                  >
                    <div className="p-2.5 bg-purple-50 border border-purple-200 rounded-lg text-[11px] text-purple-800 font-semibold space-y-1">
                      <div>UPI ID: {pmConfig.upi.vpa || 'Set UPI ID in Admin Settings'}</div>
                      <div>Merchant: {pmConfig.upi.merchantName || 'Chandana Aura'}</div>
                    </div>
                  </PMCard>
                )}

                {pmConfig?.bankTransfer?.isEnabled && (
                  <PMCard
                    id="bank_transfer"
                    title="Bank Transfer"
                    note="NEFT / IMPS"
                  >
                    <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-[11px] text-slate-800 font-semibold space-y-1">
                      <div>Bank: {pmConfig.bankTransfer.bankName || 'Set bank details in Admin Settings'}</div>
                      <div>Account: {pmConfig.bankTransfer.accountName || '-'} {pmConfig.bankTransfer.accountNumber ? `(${pmConfig.bankTransfer.accountNumber})` : ''}</div>
                      <div>IFSC: {pmConfig.bankTransfer.ifscCode || '-'}</div>
                    </div>
                  </PMCard>
                )}

              </div>
            </div>
          </div>

          {/* ── ORDER SUMMARY SIDEBAR ── */}
          <div className="sticky top-24 space-y-4">
            <div className="bg-white border border-[#E8DFD8] rounded-xl p-6 shadow-sm space-y-4">
              <h3 className="font-heading text-sm font-bold text-[#2B1810] border-b border-[#E8DFD8] pb-3">
                Order Summary
              </h3>

              {/* Items List */}
              <div className="space-y-3 max-h-52 overflow-y-auto pr-1">
                {items.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <img
                      src={item.image || '/assets/ganesha_idol.jpg'}
                      alt={item.name}
                      className="w-12 h-12 object-cover rounded-lg border border-[#E8DFD8] shrink-0"
                      onError={(e) => { e.target.src = '/assets/ganesha_idol.jpg'; }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-xs text-[#2B1810] truncate">{item.name}</p>
                      {item.variantLabel && <p className="text-[10px] text-[#8A796C]">{item.variantLabel}</p>}
                      <p className="text-[10px] text-[#C49A45] font-bold">Qty: {item.qty}</p>
                    </div>
                    <span className="text-xs font-bold text-[#2B1810] shrink-0">
                      ₹{(item.price * item.qty).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>

              {/* Promo Code & Available Coupons Section */}
              <div className="border-t border-[#E8DFD8] pt-3 space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-[#2B1810] flex items-center gap-1.5">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#C49A45" strokeWidth="2.5"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                    <span>Have a Promo Code?</span>
                  </span>
                  {availablePromos.length > 0 && (
                    <span className="text-[9.5px] font-bold text-[#C49A45] bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                      {availablePromos.length} Offers Available
                    </span>
                  )}
                </div>

                {appliedPromo ? (
                  <div className="flex items-center justify-between border border-emerald-300 bg-emerald-50/90 rounded-lg p-2.5 text-xs shadow-2xs animate-in fade-in duration-200">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[10px] font-bold shrink-0">
                        ✓
                      </div>
                      <div>
                        <p className="font-bold text-emerald-900 text-xs tracking-wide">
                          {appliedPromo.code} APPLIED
                        </p>
                        <p className="text-[10.5px] text-emerald-700 font-semibold">
                          You save ₹{discountTotal.toFixed(2)} on this order
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setAppliedPromo(null);
                        setPromoInput('');
                      }}
                      className="px-2 py-1 text-[10px] font-bold uppercase text-red-700 hover:text-red-900 hover:bg-red-50 rounded transition-colors"
                      title="Remove promo code"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <form onSubmit={applyPromo} className="flex h-10 overflow-hidden rounded-lg border border-[#D9D0C8] focus-within:border-[#C49A45] focus-within:ring-1 focus-within:ring-[#C49A45]">
                    <input
                      value={promoInput}
                      onChange={(e) => setPromoInput(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}
                      placeholder="ENTER PROMO CODE"
                      className="min-w-0 flex-1 px-3 text-xs font-bold uppercase tracking-wider outline-none text-[#2B1810] placeholder-[#A0938A]"
                      maxLength="30"
                    />
                    <button
                      type="submit"
                      disabled={promoLoading || !promoInput.trim()}
                      className="bg-[#2B1810] px-4 text-[10px] font-bold uppercase tracking-wider text-white hover:bg-[#C49A45] transition-colors disabled:opacity-40 disabled:hover:bg-[#2B1810] shrink-0"
                    >
                      {promoLoading ? 'Checking...' : 'Apply'}
                    </button>
                  </form>
                )}

                {/* Available Promo Codes Card List */}
                {availablePromos.length > 0 && (
                  <div className="mt-2 space-y-2 pt-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#8A796C]">
                        Available Coupons:
                      </span>
                      {availablePromos.length > 2 && (
                        <button
                          type="button"
                          onClick={() => setShowAllPromos(!showAllPromos)}
                          className="text-[10px] font-bold text-[#C49A45] hover:underline"
                        >
                          {showAllPromos ? 'Show Less ↑' : `View All (${availablePromos.length}) ↓`}
                        </button>
                      )}
                    </div>

                    <div className="space-y-2">
                      {(showAllPromos ? availablePromos : availablePromos.slice(0, 2)).map((promo) => {
                        const isApplicable = itemsTotal >= (promo.minOrderAmount || 0);
                        const isCurrentlyApplied = appliedPromo?.code === promo.code;
                        const discountLabel = promo.discountType === 'percentage'
                          ? `${promo.value}% OFF`
                          : `₹${promo.value} OFF`;

                        return (
                          <div
                            key={promo._id || promo.code}
                            className={`p-2.5 rounded-lg border transition-all ${
                              isCurrentlyApplied
                                ? 'bg-emerald-50/50 border-emerald-300'
                                : 'bg-[#FAF8F5] border-[#E8DFD8] hover:border-[#C49A45]/80 hover:bg-amber-50/30'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="px-2 py-0.5 text-[10px] font-mono font-bold tracking-wider rounded border border-dashed border-[#C49A45] bg-white text-[#2B1810]">
                                    {promo.code}
                                  </span>
                                  <span className="text-[9.5px] font-bold px-1.5 py-0.2 rounded bg-[#C49A45]/15 text-[#8C6B28]">
                                    {discountLabel}
                                  </span>
                                </div>
                                {promo.description && (
                                  <p className="text-[10.5px] text-[#54453D] mt-1 leading-tight font-medium line-clamp-2">
                                    {promo.description}
                                  </p>
                                )}
                                {promo.minOrderAmount > 0 && (
                                  <p className="text-[9.5px] text-[#8A796C] mt-0.5">
                                    {isApplicable ? (
                                      <span>Min. order ₹{promo.minOrderAmount}</span>
                                    ) : (
                                      <span className="text-amber-800 font-semibold">
                                        Add ₹{(promo.minOrderAmount - itemsTotal).toFixed(0)} more to unlock
                                      </span>
                                    )}
                                  </p>
                                )}
                              </div>

                              <div>
                                {isCurrentlyApplied ? (
                                  <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-1 rounded">
                                    Applied
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => applyPromo(promo.code)}
                                    disabled={promoLoading || !isApplicable}
                                    className={`px-3 py-1 text-[10px] font-bold uppercase rounded transition-all shadow-2xs ${
                                      isApplicable
                                        ? 'bg-[#C49A45] hover:bg-[#B08736] text-white hover:scale-105 cursor-pointer'
                                        : 'bg-[#E8DFD8] text-[#8A796C] cursor-not-allowed'
                                    }`}
                                  >
                                    {isApplicable ? 'Apply' : 'Locked'}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Price Breakdown */}
              <div className="border-t border-[#E8DFD8] pt-3 space-y-2 text-xs">
                <div className="flex justify-between text-[#8A796C]">
                  <span>Items ({items.reduce((s, i) => s + i.qty, 0)})</span>
                  <span>₹{itemsTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#8A796C]">
                  <span>Shipping{quote?.distanceKm ? ` (${quote.distanceKm}km)` : ''}</span>
                  <span>{quoting ? '...' : `₹${shippingCost.toFixed(2)}`}</span>
                </div>
                {discountTotal > 0 && (
                  <div className="flex justify-between font-semibold text-emerald-700">
                    <span>Promo discount</span>
                    <span>-₹{discountTotal.toFixed(2)}</span>
                  </div>
                )}
                {codFee > 0 && (
                  <div className="flex justify-between text-[#8A796C]">
                    <span>COD Fee</span>
                    <span>+₹{codFee.toFixed(2)}</span>
                  </div>
                )}
                {taxTotal > 0 && (
                  <div className="flex justify-between text-xs text-[#6B5B50]">
                    <span>Tax</span>
                    <span>+₹{taxTotal.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-sm text-[#2B1810] border-t border-[#E8DFD8] pt-2 mt-1">
                  <span>Grand Total</span>
                  <span className="text-[#C49A45] text-base">₹{grandTotal.toFixed(2)}</span>
                </div>
              </div>

              {/* Place Order Button */}
              <button
                type="button"
                disabled={placing}
                onClick={handlePlaceOrder}
                className="w-full bg-[#C49A45] hover:bg-[#B08736] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold text-xs uppercase tracking-widest py-4 rounded-xl shadow-lg transition-all hover:scale-[1.02] flex items-center justify-center gap-2.5"
              >
                {placing ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                    </svg>
                    Processing Order...
                  </>
                ) : (
                  <>
                    {paymentMethod === 'cod' ? 'Place COD Order' : ['razorpay', 'stripe', 'paypal'].includes(paymentMethod) ? 'Pay Securely' : 'Place Order'} — ₹{grandTotal.toFixed(2)}
                  </>
                )}
              </button>

              <p className="text-[10px] text-center text-[#8A796C]">
                By placing your order you agree to our <Link to="/terms-and-conditions" className="underline hover:text-[#C49A45]">Terms</Link> & <Link to="/privacy-policy" className="underline hover:text-[#C49A45]">Privacy Policy</Link>
              </p>
            </div>

            {/* Trust Badges */}
            <div className="bg-white border border-[#E8DFD8] rounded-xl p-4 grid grid-cols-2 gap-3 text-center text-[10px] text-[#8A796C]">
              <div>🔒 SSL Secured</div>
              <div>✓ Authentic Product</div>
              <div>🚚 Fast Dispatch</div>
              <div>↩ 7-Day Returns</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
